import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { BotMessageSquare } from 'lucide-react';
import { z } from 'zod';

import { SendMessage } from '@/api/fe-api/view/mutations/ai/send-message';
import { GetChatHistory } from '@/api/fe-api/view/queries/ai/get-chat-history';
import { TextareaInput } from '@/components/form/inputs/textarea-input';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePageContext } from '@/providers/page-context';

import { AiChatboxFeed } from './ai-chatbox-feed';
import { chatStyle } from './style';
import { parseStream } from './utils';

export function AiChatbox() {
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const chatHistory = GetChatHistory.useQuery();
  const sendMessage = SendMessage.useMutation();
  const featureFlag = useFeatureFlag();
  const [streamedMessage, setStreamedMessage] = useState('');
  const [submittedMessage, setSubmittedMessage] = useState('');
  const { urlKey, pageConfig, filters } = usePageContext();

  const form = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
    defaultValues: { prompt: '' },
  });

  const style = chatStyle({
    hasMessages:
      Boolean(submittedMessage) ||
      (chatHistory.data?.chatHistory &&
        chatHistory.data.chatHistory.length > 0),
  });

  async function handleSubmit(data: FormInputs) {
    setStreamedMessage('');
    setSubmittedMessage(data.prompt);
    const stream = await sendMessage.mutateAsync({
      ...data,
      page: pageConfig?.nameFull ?? urlKey ?? location.pathname,
      filters,
    });

    if (stream) {
      parseStream(stream, setStreamedMessage);
      chatHistory.refetch();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitRef.current?.click();
      event.stopPropagation();
    }
  }

  useEffect(
    () => {
      chatHistory.refetch();
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (chatHistory.data?.chatHistory) {
      setStreamedMessage((v) => (v.startsWith(errorMessage) ? v : ''));
      setSubmittedMessage('');
    }
  }, [chatHistory.data?.chatHistory]);

  if (!featureFlag('ai')) {
    return null;
  }

  return (
    <div className={style.container()}>
      <div className={style.header()}>
        <div className="mb-8 flex items-center justify-between p-4 text-neutral-500">
          <h2 className="group flex cursor-default items-center gap-2 font-condensed text-lg font-semibold hover:text-neutral-600">
            <BotMessageSquare className="text-neutral-300 group-hover:text-neutral-400" />
            CURIE AI
          </h2>
          <Button
            className={style.buttonReset()}
            variant="outline"
            data-testid="ai-refresh-session"
            onClick={() => handleSubmit({ prompt: '/reset' })}
          >
            Refresh session
          </Button>
        </div>
      </div>
      <div className={style.body()}>
        <AiChatboxFeed
          onSubmitPrompt={(prompt) => handleSubmit({ prompt })}
          streamedMessage={streamedMessage}
          submittedMessage={submittedMessage}
          isLoading={sendMessage.isLoading}
        />
        <Form {...form}>
          <form
            className={style.form()}
            onSubmit={form.handleSubmit((v) => {
              handleSubmit(v);
              form.reset();
            })}
          >
            <FormField
              control={form.control}
              name="prompt"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TextareaInput
                      {...field}
                      className={style.textArea()}
                      placeholder="Talk to Curie AI"
                      onKeyDown={handleKeyDown}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <button
              ref={submitRef}
              type="submit"
              disabled={!form.formState.isValid}
              className="hidden"
            />
          </form>
        </Form>
      </div>
    </div>
  );
}

const formSchema = z.object({
  prompt: z.string().min(1),
});

type FormInputs = z.infer<typeof formSchema>;

const errorMessage = 'Request too large';
